import { Box, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { normalizeColumnSpan } from "@/features/app-studio/lib/appChartLayout";

type AppChartColumnSpanSelectProps = {
  value: number;
  disabled?: boolean;
  onChange: (columnSpan: number) => void;
};

// Each block represents 2 columns; 6 blocks × 2 = 12-column grid
const BLOCK_OPTIONS = [
  { span: 2, tooltipKey: "build.charts.columnSpan.extraSmall" },
  { span: 4, tooltipKey: "build.charts.columnSpan.small" },
  { span: 6, tooltipKey: "build.charts.columnSpan.medium" },
  { span: 8, tooltipKey: "build.charts.columnSpan.large" },
  { span: 10, tooltipKey: "build.charts.columnSpan.extraLarge" },
  { span: 12, tooltipKey: "build.charts.columnSpan.full" },
] as const;

function resolveBlockIndex(span: number): number {
  return Math.min(5, Math.ceil(span / 2) - 1);
}

export function AppChartColumnSpanSelect({
  value,
  disabled = false,
  onChange,
}: AppChartColumnSpanSelectProps) {
  const { t } = useTranslation("appStudio");
  const selectedBlockIndex = resolveBlockIndex(normalizeColumnSpan(value));

  return (
    <Box sx={{ display: "flex", gap: "2px", alignItems: "center" }}>
      {BLOCK_OPTIONS.map(({ span, tooltipKey }, blockIndex) => {
        const isFilled = blockIndex <= selectedBlockIndex;

        return (
          <Tooltip key={span} title={t(tooltipKey)} placement="top" arrow>
            <Box
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-label={t(tooltipKey)}
              aria-pressed={blockIndex === selectedBlockIndex}
              aria-disabled={disabled}
              onClick={() => !disabled && onChange(span)}
              onKeyDown={(event) => {
                if (!disabled && (event.key === "Enter" || event.key === " ")) {
                  event.preventDefault();
                  onChange(span);
                }
              }}
              sx={{
                width: 14,
                height: 14,
                borderRadius: "3px",
                border: isFilled
                  ? "2px solid var(--theme-primary)"
                  : "1.5px solid var(--theme-border)",
                backgroundColor: isFilled
                  ? "var(--theme-primary)"
                  : "transparent",
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.5 : 1,
                transition: "border-color 0.15s, background-color 0.15s",
                "&:hover": !disabled
                  ? {
                      borderColor: "var(--theme-primary)",
                      backgroundColor: isFilled
                        ? "var(--theme-hover-primary)"
                        : "var(--theme-surface-hover)",
                    }
                  : undefined,
                "&:focus-visible": {
                  outline: "2px solid var(--theme-primary)",
                  outlineOffset: "2px",
                },
              }}
            />
          </Tooltip>
        );
      })}
    </Box>
  );
}
